import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusCodes } from 'http-status-codes';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useProfileFields from '../../../hooks/useProfileFields';
import { ProfileDataType, ProfileField } from '../../../types/profile';
import { DataRequest, InviteType } from '../../../types/invites';
import { connectionService, invitationService, profileService } from '../../../services';
import { InvalidAccountResponse } from '../../../types/responses';
import UserDataForm from '../../../components/forms/userData/UserDataForm';
import ResponsePageWrapper from '../../../components/layouts/ResponsePageWrapper';
import InvalidAuthBody from '../../../components/misc/InvalidAuthBody';
import useAuth from '../../../hooks/useAuth';
import WarningModal from '../../../components/modals/WarningModal';
import { SuccessPageState } from '../../../types/navigation';
import NoDataSharedModal from '../../../components/modals/NoDataSharedModal';
import Button from '../../../components/misc/Button';
import useMagicLogin from '../../../hooks/useMagicLogin';
import useDeleteParams from '../../../hooks/useDeleteParams';

export default function DataRequestPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  const auth = useAuth();
  const location = useLocation();

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  const invitationId = params.get('invitationId');
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [invalidAccountInfo, setInvalidAccountInfo] = useState<InvalidAccountResponse>();
  const [dataRequest, setDataRequest] = useState<DataRequest>(location.state?.request ?? undefined);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [noDataSharedModalOpen, setNoDataSharedModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(!location.state?.request);

  useEffect(() => {
    if (!invitationId && !location.state?.request) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }
    if (location.state?.request) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    invitationService
      .getDataRequest(invitationId!)
      .then((datReq): void => {
        setDataRequest(datReq);
        setInvalidAccountInfo(undefined);
        setIsLoading(false);
      })
      .catch((err: AxiosError<InvalidAccountResponse>): void => {
        setIsLoading(false);
        if (err.response?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
          setInvalidAccountInfo(err.response.data);
        } else {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        }
      });
  }, [JSON.stringify(auth)]);

  const fetchedShared = useRef<boolean>(false);
  useEffect((): void => {
    if (!dataRequest || profileFields.length === 0 || fetchedShared.current) return;

    // Check if senderId exists before calling toString
    if (!dataRequest.senderId) {
      toast.error(t('toast.error.general.invalidDataRequest'));
      navigate('/');
      return;
    }

    connectionService
      .getSharedDataWithUser(dataRequest.senderId.toString())
      .then((data) => {
        setSelectedData(data.filter((f) => dataRequest?.requestedProps.includes(f.dataType)));
      })
      .catch((): void => {});
    fetchedShared.current = true;
  }, [dataRequest, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setNoDataSharedModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!dataRequest) return;

    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    await connectionService.acceptDataRequest(
      { token: invitationId ?? undefined, dataRequestId: dataRequest.dataRequestId },
      allSelectedData.map((f) => f.id!),
    );

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.dataRequest.accepted',
      translationOptions: {
        requesterName: dataRequest?.requesterAlias?.split('+')[0] || '',
      },
      requesterId: dataRequest?.senderId!,
      requesterAlias: dataRequest?.requesterAlias,
    };

    navigate('/success', { state });
  };

  const handleDecline = async (): Promise<void> => {
    if (!dataRequest) return;
    await connectionService.declineDataRequest(dataRequest?.dataRequestId!);
    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.dataRequest.rejected',
      translationOptions: {
        requesterName: dataRequest?.requesterAlias?.split('+')[0] || '',
      },
    };
    navigate('/success', { state });
  };

  const requesterName = dataRequest?.requesterAlias?.split('+')[0] || '';

  // Show loading state
  if (isLoading) {
    return (
      <ResponsePageWrapper>
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="mb-4 text-lg font-medium">{t('general.loading')}</div>
            <div className="text-sm text-gray-600">{t('page.dataRequest.loading')}</div>
          </div>
        </div>
      </ResponsePageWrapper>
    );
  }

  // Show error if data request is invalid or missing
  if (!dataRequest || !dataRequest.senderId) {
    return (
      <ResponsePageWrapper>
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="mb-4 text-lg font-medium text-error">{t('page.dataRequest.error.title')}</div>
            <div className="mb-4 text-sm text-gray-600">{t('page.dataRequest.error.description')}</div>
            <Button variant="primary" onClick={() => navigate('/')}>
              {t('general.goHome')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
    );
  }

  if (invalidAccountInfo)
    return (
      <ResponsePageWrapper>
        <InvalidAuthBody info={invalidAccountInfo} invitationId={invitationId!} inviteType={InviteType.DATA_REQUEST} />
      </ResponsePageWrapper>
    );

  return (
    <>
      <ResponsePageWrapper>
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col">
            <h2 className="mb-4 text-2xl font-semibold">
              {t('page.dataRequest.title', { name: requesterName || '' })}
            </h2>
            <p className="text-sm">{t('page.dataRequest.description')}</p>
          </div>

          <UserDataForm
            selectedData={selectedData || []}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            onlyBirthDate
            nonRequestedFields={
              Object.keys(ProfileDataType).filter(
                (f) => !dataRequest?.requestedProps.includes(f as ProfileDataType),
              ) as ProfileDataType[]
            }
            hasSelectAll
          />
          <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button onClick={handleDecline} className="underline">
              {t('general.decline')}
            </Button>
            <Button
              variant="primary"
              className="px-[32px]"
              onClick={
                !dataRequest?.requestedProps.every((f) => selectedData.map((d) => d.dataType).includes(f))
                  ? (): void => setModalOpen(true)
                  : handleComplete
              }>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <WarningModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={t('modal.dataRequestWarning.title')}
        description={t('modal.dataRequestWarning.description')}
        buttonText={t('general.continue')}
        onSubmit={handleComplete}
      />
      <NoDataSharedModal
        open={noDataSharedModalOpen}
        setOpen={setNoDataSharedModalOpen}
        requesterName={dataRequest?.requesterAlias?.split('+')[0] || ''}
        handleComplete={handleSubmit}
      />
    </>
  );
}
